import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Reserve() {
  const { rabbitId } = useParams();
  const navigate = useNavigate();
  const [rabbit, setRabbit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [payDeposit, setPayDeposit] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/rabbits/${rabbitId}`).then((r) => setRabbit(r.data)).catch(() => {});
  }, [rabbitId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rabbit) return;
    setBusy(true);
    try {
      const inq = await api.post("/inquiries", {
        ...form,
        rabbit_id: rabbit.id,
        rabbit_name: rabbit.name,
      });
      if (payDeposit) {
        const checkout = await api.post("/checkout/deposit", {
          inquiry_id: inq.data.id,
          origin_url: window.location.origin,
        });
        window.location.href = checkout.data.url;
      } else {
        toast.success("Reservation request received! We'll contact you shortly.");
        navigate("/reserve/success?noDeposit=1");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  if (!rabbit) return <div className="p-16 text-center text-stone">Loading…</div>;

  let submitLabel = "Send Reservation Request";
  if (busy) submitLabel = "Processing…";
  else if (payDeposit) submitLabel = "Continue to Payment →";

  return (
    <div data-testid="page-reserve">
      <section className="border-b border-border bg-cream">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-20">
          <div className="section-label">Reservation</div>
          <h1 className="mt-3 font-serif text-5xl tracking-tight">Reserve {rabbit.name}</h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-stone">
            Submit your details to start the reservation. A $50 refundable deposit (applied to purchase price) holds your rabbit. Local Alabama pickup only.
          </p>
        </div>
      </section>
      <section className="mx-auto grid max-w-6xl gap-12 px-6 py-16 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <div className="border border-border bg-white">
            <div className="aspect-square overflow-hidden bg-muted">
              {rabbit.image_url && <img src={rabbit.image_url} alt={rabbit.name} className="h-full w-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="section-label">{rabbit.sex} · {rabbit.color}</div>
              <h3 className="mt-2 font-serif text-2xl">{rabbit.name}</h3>
              <div className="mt-3 flex items-end justify-between border-t border-border pt-3 text-sm">
                <span className="text-stone">Total price</span>
                <span className="font-serif text-xl text-terracotta">${rabbit.price?.toFixed(0)}</span>
              </div>
              <div className="flex items-end justify-between text-sm">
                <span className="text-stone">Deposit today</span>
                <span className="font-serif text-xl">$50</span>
              </div>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-8 space-y-5 border border-border bg-white p-8" data-testid="reserve-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone">Your name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reserve-input-name" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reserve-input-email" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reserve-input-phone" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone">Anything we should know?</label>
            <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="reserve-input-message" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <div className="border border-border bg-muted p-4">
            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input type="checkbox" checked={payDeposit} onChange={(e) => setPayDeposit(e.target.checked)} data-testid="reserve-deposit-toggle" className="mt-1" />
              <span>
                <strong>Pay the $50 refundable deposit now</strong> (Stripe — secure).
                <span className="block text-xs text-stone mt-1">Uncheck to send a no-deposit inquiry; deposit can be paid later.</span>
              </span>
            </label>
          </div>
          <button disabled={busy} data-testid="reserve-submit" className="rounded-full bg-terracotta px-8 py-3.5 text-sm text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {submitLabel}
          </button>
        </form>
      </section>
    </div>
  );
}
